import { AxiosResponse } from "axios";

import {
  ITmpOnboardingLink,
  ITmpWardRule,
  ITmpWardRuleRequest,
  ITmpWardStaffOption,
  ITmpRetryResult,
  ITmpSession,
  ITmpSessionDetail,
  ITmpSessionEvent,
  ITmpSessionFilters,
  ITmpSessionPayload,
} from "../../@types/admin/AdminTmpIntegration";
import { IPaginatedResponse } from "../../@types/PaginatedResponse";
import { api } from "../api";

const BASE_URL = "/Retool/TransmuralPlatform";

/**
 * Transmural Platform admin page (EN-5203): the sessions the TMP webhook has
 * created, their per-order actions, and the prescriber-to-ward mappings the
 * provisioner resolves wards from. Same Retool base and auth as the Jupyter
 * service — super-admin only.
 */
export const tmpIntegrationService = {
  async getSessions(
    filters: ITmpSessionFilters = {}
  ): Promise<AxiosResponse<IPaginatedResponse<ITmpSession[]>>> {
    return api.get<IPaginatedResponse<ITmpSession[]>>(`${BASE_URL}/Sessions`, {
      params: {
        status: filters.status || undefined,
        wardId: filters.wardId || undefined,
        search: filters.search?.trim() || undefined,
        page: filters.page,
        pageSize: filters.pageSize,
      },
    });
  },

  async getSession(
    telemonitoringId: string
  ): Promise<AxiosResponse<ITmpSessionDetail>> {
    return api.get<ITmpSessionDetail>(
      `${BASE_URL}/Sessions/${encodeURIComponent(telemonitoringId)}`
    );
  },

  /** The raw webhook body TMP sent for this order, as stored (masked). */
  async getSessionPayload(
    telemonitoringId: string
  ): Promise<AxiosResponse<ITmpSessionPayload>> {
    return api.get<ITmpSessionPayload>(
      `${BASE_URL}/Sessions/${encodeURIComponent(telemonitoringId)}/Payload`
    );
  },

  /** Every step the order went through, oldest first. */
  async getSessionEvents(
    telemonitoringId: string
  ): Promise<AxiosResponse<ITmpSessionEvent[]>> {
    return api.get<ITmpSessionEvent[]>(
      `${BASE_URL}/Sessions/${encodeURIComponent(telemonitoringId)}/Events`
    );
  },

  /** Runs the provisioner for this one order straight away. */
  async retrySession(
    telemonitoringId: string
  ): Promise<AxiosResponse<ITmpRetryResult>> {
    return api.post<ITmpRetryResult>(
      `${BASE_URL}/Sessions/${encodeURIComponent(telemonitoringId)}/Retry`
    );
  },

  /** Mints a fresh one-time completion link for the hospital clinician. */
  async issueOnboardingLink(
    telemonitoringId: string
  ): Promise<AxiosResponse<ITmpOnboardingLink>> {
    return api.post<ITmpOnboardingLink>(
      `${BASE_URL}/Sessions/${encodeURIComponent(
        telemonitoringId
      )}/OnboardingLink`
    );
  },

  /** Active staff of a ward (and its hospital group) for the acting-staff picker. */
  async getWardStaff(
    wardId: number
  ): Promise<AxiosResponse<ITmpWardStaffOption[]>> {
    return api.get<ITmpWardStaffOption[]>(
      `${BASE_URL}/Wards/${encodeURIComponent(String(wardId))}/Staff`
    );
  },

  async getWardRules(): Promise<AxiosResponse<ITmpWardRule[]>> {
    return api.get<ITmpWardRule[]>(`${BASE_URL}/WardRules`);
  },

  async createWardRule(
    request: ITmpWardRuleRequest
  ): Promise<AxiosResponse<ITmpWardRule>> {
    return api.post<ITmpWardRule>(`${BASE_URL}/WardRules`, request);
  },

  async updateWardRule(
    id: string,
    request: ITmpWardRuleRequest
  ): Promise<AxiosResponse<ITmpWardRule>> {
    return api.put<ITmpWardRule>(
      `${BASE_URL}/WardRules/${encodeURIComponent(id)}`,
      request
    );
  },

  async deleteWardRule(id: string): Promise<AxiosResponse<void>> {
    return api.delete<void>(`${BASE_URL}/WardRules/${encodeURIComponent(id)}`);
  },
};
